import { createApiRef } from '@backstage/frontend-plugin-api';
import type {
  MonthlySpendReport,
  OpenAIPlatformKeySpend,
  MonthlySpendReportScope,
  SpendFreshness,
} from '@internal/plugin-ai-spend-monitor-common';

/** A user's effective monthly budget; `unlimited` when uncapped. */
export type UserSpendLimit =
  | { type: 'finite'; amountUsd: string; source: string }
  | { type: 'unlimited'; source: string };

/**
 * A single user's month-to-date AI spend and effective budget, as returned by
 * `GET /api/ai-spend-monitor/me`. Mirrors the backend `UserSpendResponse`
 * contract; all USD values are decimal strings.
 */
export type UserSpend = {
  periodStart: string;
  periodEnd: string;
  observedAt: string;
  totalUsd: string;
  freshness?: SpendFreshness;
  limitFreshness?: SpendFreshness;
  limit: UserSpendLimit;
  /**
   * Run-out projection; only present for finite positive limits when the
   * backend's forecast gating passes. `limitReachedAt` is an ISO date, or null
   * when the projected spend stays within the limit.
   */
  forecast?: { projectedUsd: string; limitReachedAt: string | null };
  /** Budget-increase form; present when configured and the limit is finite. */
  budgetRequestUrl?: string;
  spendByProvider: Array<{
    provider: string;
    amountUsd: string;
    freshness?: SpendFreshness;
  }>;
};

export type MonthlySpendReportRequestOptions = {
  includeAllSpenders?: boolean;
};

export interface AiSpendMonitorApi {
  getMonthlyReport(
    month: string,
    scope: MonthlySpendReportScope,
    selectedUserEmails?: readonly string[],
    options?: MonthlySpendReportRequestOptions,
  ): Promise<MonthlySpendReport>;

  /**
   * Month-to-date spend + effective budget for the authenticated caller.
   * Resolves to `undefined` when no spend snapshot exists yet (e.g. a new
   * user, or before the first collection) — a normal empty state.
   */
  getMySpend(): Promise<UserSpend | undefined>;
  /**
   * Informational OpenAI API key spend of the whole organization for a month
   * (`YYYY-MM`). Resolves to `undefined` when the feature is not configured.
   */
  getOpenAIPlatformKeySpend(
    month: string,
  ): Promise<OpenAIPlatformKeySpend | undefined>;
}

export const aiSpendMonitorApiRef = createApiRef<AiSpendMonitorApi>().with({
  id: 'plugin.ai-spend-monitor.api',
});
